import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Icon from "../../../../components/icons";
import toast from "react-hot-toast";
import Button from "../../../../components/elements/button";
import SelectBox from "../../../../components/elements/form/selectBox";
import { useSagesApi } from "../../../../store/api/hooks/sageApiHook";
import { co } from "@fullcalendar/core/internal-common";

interface Props {
    onClose: () => void;
    onSelect: (assets: any[]) => void;
    item?: any;
    onRefresh?: () => void; // NEW
}

type ConectorsFormValueType = {
    disposalProceedAccountGroupID?: any;
    disposalProceedAccountID?: any;
};

const AssetDisposalModalContent: React.FC<Props> = ({ onClose, onSelect, item, onRefresh }) => {
    const [accGroupOption, setAccGroupOption] = useState<any[]>([]);
    const [cashBankAccOption, setCashBankAccOption] = useState<any[]>([]);
    const [prefillDone, setPrefillDone] = useState(false);

    const defaultSelectOption = { label: "Select an option", value: "" };

    const sageApi: any = useSagesApi();
    const { control, setValue, watch } = useForm<ConectorsFormValueType>({
        defaultValues: {}
    });

    const loadAccountsByGroup = (accountGroupID: number | string, selectedAccountID?: number | string) => {
        if (accountGroupID === "" || accountGroupID === undefined || accountGroupID === null) {
            setCashBankAccOption([defaultSelectOption]);
            return;
        }

        sageApi.getSyncAccount({ pageSize: 0, accountGroupID }, (resp: any) => {
            const options = [
                defaultSelectOption,
                ...(resp.sageAccountList?.map((acc: any) => ({
                    label: acc.accountTitle,
                    value: acc.accountID,
                })) || [])
            ];
            setCashBankAccOption(options);

            if (selectedAccountID !== undefined && selectedAccountID !== null) {
                const selected = options.find(opt => opt.value === selectedAccountID) || defaultSelectOption;
                setValue('disposalProceedAccountID', selected);
            }
        }, toast.error);
    };

    const getAssetOtherMapping = () => {
        sageApi.getSyncAccountGroups({ pageSize: 0 }, (groupResp: any) => {
            const accGroupOpts = [
                defaultSelectOption,
                ...(groupResp.sageAccountGroupList?.map((group: any) => ({
                    label: group.accountGroupName,
                    value: group.accountGroupID,
                })) || [])
            ];
            setAccGroupOption(accGroupOpts);

            sageApi.getAssetOtherMapping({ pageSize: 0 }, (resp: any) => {
                const data = resp;
                if (!data) return;

                const findGroup = (id: number) =>
                    accGroupOpts.find((g: any) => g.value === id) || defaultSelectOption;

                const groupID = data.disposalProceedAccountGroupID;
                const accID = data.disposalProceedAccountID;

                if (groupID !== undefined && groupID !== null) {
                    setValue('disposalProceedAccountGroupID', findGroup(groupID));
                    loadAccountsByGroup(groupID, accID);
                }

                setPrefillDone(true);
            }, toast.error);
        }, toast.error);
    };

    const postToSage = () => {
        const payload = {
            assetID: item.assetID,
            disposalID: item.assetDisposalID,
            cashAccountID: watch('disposalProceedAccountID')?.value || '',
        };
        console.log("Posting to Sage with payload:", payload);

        if (!payload.cashAccountID) {
            toast.error("Please select a valid cash/bank account before posting.");
            return;
        }

        sageApi.PostAssetDisposalToSage(
            payload,
            (resp: any) => {
                if (resp.successful) {
                    toast.success(resp.message || "Posted to Sage successfully");
                    onClose();
                    if (onRefresh) onRefresh(); // REFRESH parent data
                } else {
                    toast.error(resp.message || "Failed to post to Sage");
                }
            },
            (error: any) => {
                toast.error("Failed to post to Sage");
            }
        );
    };

    useEffect(() => {
        getAssetOtherMapping();
    }, []);

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === "disposalProceedAccountGroupID" && value.disposalProceedAccountGroupID?.value !== "") {
                loadAccountsByGroup(value.disposalProceedAccountGroupID.value);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    return (
        <div className="modal-box max-w-[90%] w-[50rem] p-4">
            {/* Sticky header */}
            <div className="flex items-center justify-between border-b border-[#8D6E63] pb-2 mb-2 sticky top-0 z-30 bg-white">
                <p className="text-[1.063rem] text-[#755B51] font-semibold">Change Bank Account</p>
                <button onClick={onClose} className="bg-primary text-white p-1 rounded-md">
                    <Icon name="close" size={1.25} />
                </button>
            </div>

            {/* Content */}
            {prefillDone && (
                <div>
                    <div className="grid grid-cols-3 gap-x-4 items-center gap-y-4 mb-4 py-6">
                        <label className="font-semibold col-span-1">Cash / Bank for Disposal Proceeds</label>

                        <Controller
                            name="disposalProceedAccountGroupID"
                            control={control}
                            render={({ field }) => (
                                <SelectBox {...field} options={accGroupOption} disabled />
                            )}
                        />

                        <Controller
                            name="disposalProceedAccountID"
                            control={control}
                            render={({ field }) => (
                                <SelectBox {...field} options={cashBankAccOption} />
                            )}
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-2 border-t border-[#8D6E63] pt-2">
                        <Button type="button" theme="secondary" onClick={onClose}>
                            Close
                        </Button>
                        <Button type="button" onClick={postToSage}>
                            Post to Sage
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetDisposalModalContent;